import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api from "@/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const pageStyles = `
.vault-page {
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 24px;
  background: radial-gradient(circle at 15% 20%, #0b7d5a 0%, #033326 60%, #022116 100%);
  gap: 14px;
}
.vault-card {
  width: 100%;
  max-width: 460px;
  background: rgba(255, 255, 255, 0.58);
  backdrop-filter: blur(22px) saturate(170%);
  border: 1px solid rgba(255, 255, 255, 0.34);
  border-radius: 22px;
  padding: 28px;
  box-shadow: 0 25px 45px rgba(0, 0, 0, 0.35);
}
.vault-title {
  font-size: 28px;
  color: #1f2937;
  margin-bottom: 8px;
}
.vault-sub {
  color: #065f46;
  font-size: 14px;
  margin-bottom: 18px;
}
.vault-logo-wrap {
  text-align: center;
  width: 100%;
  max-width: 460px;
}
.vault-logo {
  width: min(240px, 72vw);
  max-height: 130px;
  display: block;
  margin: 0 auto 8px;
  object-fit: contain;
  object-position: center;
}
.vault-caption {
  font-size: 12px;
  text-transform: uppercase;
  letter-spacing: 0.28em;
  color: #d1fae5;
}
`;

export default function VaultUnlock() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [password, setPassword] = useState("");
  const [remember, setRemember] = useState(true);
  const [loading, setLoading] = useState(false);
  const params = new URLSearchParams(location.search || "");
  const mode = String(params.get("mode") || "general").toLowerCase();
  const isSensitiveMode = mode === "sensitive";

  useEffect(() => {
    setPassword("");
  }, []);

  const submitUnlock = async (e) => {
    e.preventDefault();
    if (!password) {
      toast.error("Enter business vault password");
      return;
    }

    setLoading(true);
    try {
      const res = await api.post("/local-security/unlock", {
        password,
        remember_device: remember,
      });
      toast.success("Vault unlocked");
      navigate("/login", { replace: true });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Vault unlock failed");
    } finally {
      setLoading(false);
    }
  };

  const unlockRemembered = async () => {
    setLoading(true);
    try {
      const res = await api.post("/local-security/unlock-saved", {});
      toast.success("Vault unlocked");
      navigate("/login", { replace: true });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Remembered vault unlock failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="vault-page">
      <style>{pageStyles}</style>
      <div className="vault-logo-wrap">
        <img
          src={`${process.env.PUBLIC_URL}/jewelflow-logo-full.png?v=20260613a`}
          alt="JewelFlow"
          className="vault-logo"
          onError={(e) => {
            e.currentTarget.src = `${process.env.PUBLIC_URL}/jewelflow-logo-full.png?v=20260613a`;
          }}
        />
        <div className="vault-caption">CRAFTED FOR EFFICIENCY</div>
      </div>

      <div className="vault-card">
        <div className="vault-title">Unlock Vault</div>
        <div className="vault-sub">Enter your business vault password to continue.</div>

        <form className="space-y-4" onSubmit={submitUnlock}>
          <div className="space-y-1">
            <Label>Business Vault Password</Label>
            <Input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder={t("login.vault_password")}
            />
          </div>

          <label className="flex items-center gap-2 text-sm text-slate-700">
            <input
              type="checkbox"
              checked={remember}
              onChange={(e) => setRemember(e.target.checked)}
            />
            Remember this device
          </label>

          <Button className="w-full bg-emerald-700 hover:bg-emerald-800" disabled={loading}>
            {loading ? t("vault.unlocking", "Unlocking...") : t("vault.title", t("vault.title", "Unlock Vault"))}
          </Button>

          {!isSensitiveMode && (
            <Button
              type="button"
              variant="outline"
              className="w-full"
              onClick={unlockRemembered}
              disabled={loading}
            >
              Use Remembered Vault
            </Button>
          )}

          <Button
            type="button"
            variant="outline"
            className="w-full"
            onClick={() => navigate("/login")}
            disabled={loading}
          >
            Back to Login
          </Button>
        </form>
      </div>
    </div>
  );
}
